import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';

/**
 * Localhost Setup Helper Component
 * Provides step-by-step instructions for setting up SAP CDC on localhost
 */
const LocalhostSetupHelper = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [cookiesVerified, setCookiesVerified] = useState(false);
  const [corsFixed, setCorsFixed] = useState(false);

  const steps = [
    {
      number: 1,
      title: "Understand the CORS Issue",
      description: "SAP CDC requires proper domain cookies for authentication",
      action: "Review CORS error in console",
      details: "The error 'Access-Control-Allow-Origin' blocked occurs because localhost cannot directly access gigya.laerdal.com APIs without proper cookies.",
      type: "info"
    },
    {
      number: 2,
      title: "Open Production Environment",
      description: "Visit the production environment to establish authentication cookies",
      action: "Open https://connect-dev.laerdal.com",
      details: "This will initiate the SAP CDC authentication flow in the production environment and establish the necessary domain cookies.",
      type: "action"
    },
    {
      number: 3,
      title: "Wait for Redirect",
      description: "Allow the page to redirect to id.laerdal.com",
      action: "Wait for automatic redirect",
      details: "The connect-dev page will automatically redirect you to id.laerdal.com where SAP CDC authentication cookies are established.",
      type: "wait"
    },
    {
      number: 4,
      title: "Verify Cookies",
      description: "Check that authentication cookies are saved",
      action: "Open Developer Tools → Application → Cookies",
      details: "Look for cookies from laerdal.com domain. These cookies enable cross-origin requests to work properly.",
      type: "verify"
    },
    {
      number: 5,
      title: "Return to Localhost",
      description: "Come back to your localhost development environment",
      action: "Navigate back to http://localhost:3000",
      details: "With the proper cookies established, SAP CDC authentication should now work on localhost.",
      type: "action"
    },
    {
      number: 6,
      title: "Test SAP CDC Login",
      description: "Try the SAP CDC authentication flow",
      action: "Click 'Login with SAP CDC' button",
      details: "The CORS errors should be resolved and authentication should work properly.",
      type: "test"
    }
  ];

  const handleOpenProductionSite = () => {
    window.open('https://connect-dev.laerdal.com', '_blank');
    setCurrentStep(2);
  };

  const handleVerifyCookies = () => {
    setCookiesVerified(true);
    console.log('🔍 [Localhost Helper] Checking for SAP CDC cookies...');
    console.log('Look for cookies from these domains:');
    console.log('- id.laerdal.com');
    console.log('- connect-dev.laerdal.com');
    console.log('- *.gigya.com (SAP CDC domain)');
    setCurrentStep(4);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              🏠 Localhost SAP CDC Setup Helper
            </h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ×
            </button>
          </div>

          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <div className="text-yellow-600 mr-2">⚠️</div>
              <div>
                <p className="font-medium text-yellow-800">Why is this needed?</p>
                <p className="text-yellow-700 text-sm mt-1">
                  SAP CDC requires SSL certificates for authentication. Since localhost cannot provision 
                  proper web certificates, we need to get authentication cookies from the production 
                  environment first.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {steps.map((step) => (
              <div
                key={step.number}
                className={`border rounded-lg p-4 ${
                  currentStep === step.number 
                    ? 'border-blue-500 bg-blue-50' 
                    : currentStep > step.number 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-start">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold mr-3 ${
                    currentStep > step.number 
                      ? 'bg-green-500' 
                      : currentStep === step.number 
                        ? 'bg-blue-500' 
                        : 'bg-gray-400'
                  }`}>
                    {currentStep > step.number ? '✓' : step.number}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800">{step.title}</h3>
                    <p className="text-gray-600 text-sm mt-1">{step.description}</p>
                    <p className="font-medium text-gray-800 mt-2">{step.action}</p>
                    <p className="text-gray-500 text-xs mt-1">{step.details}</p>
                    
                    {/* Action buttons for specific steps */}
                    {step.number === 1 && currentStep === 1 && (
                      <Button 
                        onClick={handleOpenProductionSite}
                        className="mt-3 bg-blue-600 hover:bg-blue-700"
                      >
                        🌐 Open connect-dev.laerdal.com
                      </Button>
                    )}
                    
                    {step.number === 3 && currentStep === 3 && (
                      <div className="mt-3 space-y-2">
                        <Button 
                          onClick={handleVerifyCookies}
                          className="bg-orange-600 hover:bg-orange-700"
                        >
                          🔍 I've verified the cookies
                        </Button>
                        <p className="text-xs text-gray-500">
                          Press F12 → Network tab → Look for cookies from id.laerdal.com
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-800 mb-2">🔧 Technical Details:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• SAP CDC uses domain-based cookies for authentication</li>
              <li>• localhost cannot establish SSL certificates required by SAP CDC</li>
              <li>• By visiting the production environment first, we establish the necessary cookies</li>
              <li>• These cookies are then available for localhost SAP CDC authentication</li>
              <li>• Alternative: Use a staging environment with proper SSL setup</li>
            </ul>
          </div>

          {currentStep >= 5 && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <div className="text-green-600 mr-2">✅</div>
                <div>
                  <p className="font-medium text-green-800">Setup Complete!</p>
                  <p className="text-green-700 text-sm mt-1">
                    You can now close this helper and try SAP CDC login on localhost.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-between">
            <Button 
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1}
              variant="outline"
            >
              ← Previous Step
            </Button>
            
            <Button 
              onClick={() => setCurrentStep(Math.min(5, currentStep + 1))}
              disabled={currentStep === 5}
            >
              Next Step →
            </Button>
            
            <Button onClick={onClose} variant="outline">
              Close Helper
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocalhostSetupHelper;
